/**
 * Armador del Estado de Pago.
 *
 * Éste es el cierre del círculo: se cargan las guías en planta y el EP sale
 * solo, sin transcribir nada. Reemplaza la cadena
 * GUIAS → SUMIFS → FISICO-FINANCIERO → CARATULA del Excel.
 *
 * Corrige el defecto S7: el total del EP era una suma explícita de 19 celdas
 * (`=L8+L51+L88+…`), así que agregar un capítulo no lo incluía. Aquí se agrega
 * por capítulo, siempre.
 *
 * Ver ANALISIS-PLANILLAS.md §3.5 y §3.2, PROMPT-MAESTRO §5.5.
 */

import type {
  Capitulo,
  Guia,
  ItemContrato,
  LineaEP,
  RecargoCalculado,
  TotalesEP,
} from './tipos';
import { roundTo, precioVigente, ufAClp, IVA } from './precios';

/** Estados de guía que entran al estado de pago. */
const ESTADOS_FACTURABLES = new Set(['EMITIDA', 'FACTURADA']);

export interface GuiaConRecargos {
  guia: Pick<Guia, 'id' | 'fecha' | 'cantidadM3' | 'itemContratoId' | 'estado'>;
  recargos: RecargoCalculado[];
}

export interface EntradaEP {
  periodoDesde: string; // yyyy-MM-dd
  periodoHasta: string; // yyyy-MM-dd
  valorUf: number;
  items: (ItemContrato & { precioUf?: number })[];
  capitulos: Capitulo[];
  guias: GuiaConRecargos[];
  /** Acumulado físico por partida al cierre del EP anterior. */
  acumuladoAnterior: Record<string, { cantidad: number; montoUf: number }>;
  /** Cuota de gastos generales del mes (1 = mes completo). Partida 8.1. */
  factorGastosGenerales?: number;
  /** Cuota de gastos COVID del mes. Partida 9.1. */
  factorGastosCovid?: number;
  /** Ajustes manuales por código de partida. */
  ajustes?: Record<string, { cantidad: number; motivo: string }>;
  factorReajuste?: number;
}

export interface ResultadoEP {
  lineas: LineaEP[];
  totales: TotalesEP;
  /** Guías que no pudieron imputarse a ninguna partida. */
  guiasSinPartida: string[];
  advertencias: string[];
}

const ITEM_GASTOS_GENERALES = '8.1';
const ITEM_GASTOS_COVID = '9.1';

function enRango(fecha: string, desde: string, hasta: string): boolean {
  return fecha >= desde && fecha <= hasta;
}

/**
 * Arma el estado de pago completo a partir de las guías del período.
 *
 * Para cada partida del contrato:
 *   cant_periodo = Σ m³ de guías imputadas + Σ cantidades de recargos
 *                  + cuota de gastos generales/COVID + ajuste manual
 *   monto_periodo_uf   = cant_periodo × precio_uf
 *   cant_acum_fecha    = acumulado_anterior + cant_periodo
 */
export function armarEstadoPago(e: EntradaEP): ResultadoEP {
  const advertencias: string[] = [];
  const guiasSinPartida: string[] = [];

  // Índices de trabajo
  const itemPorId = new Map(e.items.map((i) => [i.id, i]));
  const itemPorCodigo = new Map(e.items.map((i) => [i.codigo, i]));
  const capituloPorNumero = new Map(e.capitulos.map((c) => [c.numero, c]));

  /** cantidad acumulada del período, por código de partida */
  const cantidades = new Map<string, number>();
  const sumar = (codigo: string, cantidad: number) => {
    if (!Number.isFinite(cantidad) || cantidad === 0) return;
    cantidades.set(codigo, (cantidades.get(codigo) ?? 0) + cantidad);
  };

  // ── 1. Guías del período ────────────────────────────────────────────────
  for (const { guia, recargos } of e.guias) {
    if (!enRango(guia.fecha, e.periodoDesde, e.periodoHasta)) continue;
    if (!ESTADOS_FACTURABLES.has(guia.estado)) continue;

    // Cantidad principal → partida de la guía
    if (guia.itemContratoId) {
      const item = itemPorId.get(guia.itemContratoId);
      if (item) {
        sumar(item.codigo, guia.cantidadM3);
      } else {
        guiasSinPartida.push(guia.id);
      }
    } else {
      guiasSinPartida.push(guia.id);
    }

    // Recargos derivados → sus propias partidas
    for (const r of recargos) {
      if (!itemPorCodigo.has(r.itemCodigo)) {
        advertencias.push(
          `La partida ${r.itemCodigo} (${r.regla}) no existe en el contrato: el recargo no se facturó.`,
        );
        continue;
      }
      sumar(r.itemCodigo, r.cantidad);
    }
  }

  if (guiasSinPartida.length > 0) {
    advertencias.push(
      `${guiasSinPartida.length} guía(s) del período sin partida asignada: no entraron al EP.`,
    );
  }

  // ── 2. Cuotas mensuales fijas ───────────────────────────────────────────
  if (e.factorGastosGenerales) sumar(ITEM_GASTOS_GENERALES, e.factorGastosGenerales);
  if (e.factorGastosCovid) sumar(ITEM_GASTOS_COVID, e.factorGastosCovid);

  // ── 3. Ajustes manuales ─────────────────────────────────────────────────
  for (const [codigo, aj] of Object.entries(e.ajustes ?? {})) {
    if (!itemPorCodigo.has(codigo)) {
      advertencias.push(`Ajuste manual sobre una partida inexistente: ${codigo}.`);
      continue;
    }
    sumar(codigo, aj.cantidad);
  }

  // ── 4. Una línea por partida del contrato ───────────────────────────────
  const lineas: LineaEP[] = e.items.map((item) => {
    const precioUf = item.precioUf ?? precioVigente(item, e.factorReajuste);
    const cantPeriodo = roundTo(cantidades.get(item.codigo) ?? 0, 4);
    const anterior = e.acumuladoAnterior[item.codigo] ?? { cantidad: 0, montoUf: 0 };
    const ajuste = e.ajustes?.[item.codigo];

    const montoPeriodoUf = roundTo(cantPeriodo * precioUf, 4);
    const cantAcumFecha = roundTo(anterior.cantidad + cantPeriodo, 4);
    const montoAcumFechaUf = roundTo(anterior.montoUf + montoPeriodoUf, 4);

    return {
      itemContratoId: item.id,
      itemCodigo: item.codigo,
      descripcion: item.descripcion,
      unidad: item.unidad,
      capituloNumero: item.capituloNumero,
      cantAcumAnterior: anterior.cantidad,
      cantPeriodo,
      cantAcumFecha,
      precioUf,
      montoAcumAnteriorUf: anterior.montoUf,
      montoPeriodoUf,
      montoAcumFechaUf,
      ajusteManual: ajuste?.cantidad ?? 0,
      motivoAjuste: ajuste?.motivo ?? null,
    };
  });

  // ── 5. Totales, agregando por capítulo (corrige S7) ─────────────────────
  const porCapituloMap = new Map<number, number>();
  for (const l of lineas) {
    if (l.montoPeriodoUf === 0) continue;
    porCapituloMap.set(l.capituloNumero, (porCapituloMap.get(l.capituloNumero) ?? 0) + l.montoPeriodoUf);
  }

  const porCapitulo = [...porCapituloMap.entries()]
    .sort(([a], [b]) => a - b)
    .map(([capituloNumero, montoUf]) => ({
      capituloNumero,
      nombre: capituloPorNumero.get(capituloNumero)?.nombre ?? `Capítulo ${capituloNumero}`,
      montoUf: roundTo(montoUf, 4),
    }));

  const totalUf = roundTo(
    porCapitulo.reduce((s, c) => s + c.montoUf, 0),
    3,
  );
  const totalClp = ufAClp(totalUf, e.valorUf);
  const ivaClp = Math.round(totalClp * IVA);

  return {
    lineas,
    totales: {
      totalUf,
      valorUf: e.valorUf,
      totalClp,
      ivaClp,
      totalFacturarClp: totalClp + ivaClp,
      porCapitulo,
    },
    guiasSinPartida,
    advertencias,
  };
}

/**
 * Resumen por capítulo para la HES de CODELCO
 * (hoja «HES» del Excel: VLOOKUP del total de cada capítulo).
 */
export function resumenHes(r: ResultadoEP, capitulos: Capitulo[]) {
  const porCap = new Map(r.totales.porCapitulo.map((c) => [c.capituloNumero, c.montoUf]));
  const filas = capitulos
    .slice()
    .sort((a, b) => a.orden - b.orden)
    .map((c) => ({
      numero: c.numero,
      nombre: c.nombre,
      montoUf: roundTo(porCap.get(c.numero) ?? 0, 3),
    }));
  return {
    filas,
    totalUf: r.totales.totalUf,
    valorUf: r.totales.valorUf,
    totalClp: r.totales.totalClp,
    ivaClp: r.totales.ivaClp,
    totalFacturarClp: r.totales.totalFacturarClp,
  };
}

/**
 * Validaciones previas al cierre de un EP.
 * Devuelve los impedimentos (bloquean) y las advertencias (avisan).
 */
export function validarCierreEP(params: {
  guiasBorrador: number;
  guiasSinClasificar: number;
  guiasIncompletas: number;
  guiasSinPartida: number;
  contratoExcedido: boolean;
  exigirTiemposCompletos?: boolean;
}): { impedimentos: string[]; advertencias: string[]; puedeCerrar: boolean } {
  const impedimentos: string[] = [];
  const advertencias: string[] = [];

  if (params.guiasBorrador > 0)
    impedimentos.push(`Hay ${params.guiasBorrador} guía(s) en borrador sin emitir.`);
  if (params.guiasSinClasificar > 0)
    impedimentos.push(`Hay ${params.guiasSinClasificar} guía(s) con producto sin clasificar.`);
  if (params.guiasSinPartida > 0)
    impedimentos.push(`Hay ${params.guiasSinPartida} guía(s) sin partida de contrato asignada.`);

  if (params.guiasIncompletas > 0) {
    const msg =
      `Hay ${params.guiasIncompletas} guía(s) sin los 6 horarios: no se les puede calcular sobrestadía.`;
    if (params.exigirTiemposCompletos) impedimentos.push(msg);
    else advertencias.push(msg);
  }

  if (params.contratoExcedido)
    advertencias.push(
      'El contrato está excedido. El cierre requiere confirmación del jefe de contrato.',
    );

  return { impedimentos, advertencias, puedeCerrar: impedimentos.length === 0 };
}
